const ANONYMOUS_OWNER_ID = 'wx-copilot-anonymous-owner';

class WxCopilotContextManager {
  constructor() {
    this.currentContext = null;
    this.pageSpecificContext = {};
    this.debounceTimer = null;
    this.retryTimer = null;
    this.mountId = 'wx-copilot-root';
    this.DEBOUNCE_DELAY = 500;
    this.RETRY_DELAY = 200;
    this.MAX_RETRIES = 10;
    this.applicantTableOwnerId = null;
    // ownerId -> payload, insertion-ordered (an owner's position is set once,
    // on its first-ever call, and never moves — see setApplicantTableContext).
    // Lets a second still-mounted owner's context survive when the current
    // owner unmounts, instead of going blank until the survivor's own next
    // re-render.
    this.applicantTableContexts = new Map();
    // ownerId -> the bulkSelect/selectedApplicantIds that owner last set —
    // kept separately from applicantTableContexts (which also holds
    // display-only fields like `applicants`/`attended`) so a re-render that
    // only touches those display fields can be told apart from a genuine
    // selection change. See setApplicantTableContext.
    //
    // Cleared for an ownerId whenever that owner's context is cleared (see
    // the `applicantTable === undefined` branch below) — the hook only ever
    // calls with undefined on a true unmount or a genuine data-present ->
    // absent transition, never on an ordinary re-render, so there's no risk
    // of erasing a comparison baseline a same-pass call still needs. Without
    // this, a table that drains to empty and later repopulates with the
    // exact same selection it had before draining would compare against its
    // own stale pre-drain entry, read as "unchanged", and never reclaim
    // ownership from whoever was promoted while it was empty.
    this.applicantTableSelections = new Map();
  }

  sendPageContext(url, mountId = 'wx-copilot-root') {
    this.mountId = mountId;
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = setTimeout(() => {
      this.flushPageContext(url, mountId, 0);
    }, this.DEBOUNCE_DELAY);
  }

  setWorkflowContext(workflow) {
    const changed =
      JSON.stringify(this.pageSpecificContext.workflow) !==
      JSON.stringify(workflow);
    this.pageSpecificContext.workflow = workflow;
    if (changed && this.currentContext) {
      this.sendContextUpdate();
    }
  }

  setApplicantContext(applicant) {
    const changed =
      JSON.stringify(this.pageSpecificContext.applicant) !==
      JSON.stringify(applicant);
    this.pageSpecificContext.applicant = applicant;
    if (changed && this.currentContext) {
      this.sendContextUpdate();
    }
  }

  setApplicantTableContext(applicantTable, ownerId = ANONYMOUS_OWNER_ID) {
    if (applicantTable === undefined) {
      this.applicantTableContexts.delete(ownerId);
      this.applicantTableSelections.delete(ownerId);
      // Promote whichever surviving owner registered most recently (i.e. is
      // last in Map insertion order) and is still mounted. A plain data
      // update never moves an owner's position — only a fresh registration
      // does — so this reflects mount recency, not update recency. Only
      // runs when the departing owner actually was current —
      // an unrelated owner's unmount must not reassign ownership away from
      // whoever the user is actively working with.
      if (this.applicantTableOwnerId === ownerId) {
        const entries = [...this.applicantTableContexts];
        const [nextOwnerId, nextPayload] = entries[entries.length - 1] ?? [
          null,
          undefined,
        ];
        this.applicantTableOwnerId = nextOwnerId;
        this._publishApplicantTable(
          this._withOtherSelectionsFlag(nextPayload, nextOwnerId),
        );
      } else if (this.applicantTableOwnerId) {
        // A non-current roster closing never reassigns ownership, but it can
        // change hasOtherOpenSelections: the current owner's last-published
        // payload was flagged while the departing roster's selection was
        // still open. Republish so the flag reflects the registry as it now
        // stands — _publishApplicantTable no-ops when nothing changed.
        const currentPayload = this.applicantTableContexts.get(
          this.applicantTableOwnerId,
        );
        this._publishApplicantTable(
          this._withOtherSelectionsFlag(
            currentPayload,
            this.applicantTableOwnerId,
          ),
        );
      }
      return;
    }

    const previousSelection = this.applicantTableSelections.get(ownerId);
    const nextSelection = {
      bulkSelect: applicantTable.bulkSelect,
      selectedApplicantIds: applicantTable.selectedApplicantIds,
    };
    // A first registration only counts as a selection change when it carries
    // a real selection. Merely opening a second panel (empty selection) must
    // not steal ownership from a table the user has checkboxes ticked in —
    // that published "nothing selected" over a live selection.
    const selectionChanged = previousSelection
      ? previousSelection.bulkSelect !== nextSelection.bulkSelect ||
        JSON.stringify(previousSelection.selectedApplicantIds) !==
          JSON.stringify(nextSelection.selectedApplicantIds)
      : Boolean(
          nextSelection.bulkSelect ||
            (nextSelection.selectedApplicantIds?.length ?? 0) > 0,
        );

    this.applicantTableContexts.set(ownerId, applicantTable);
    this.applicantTableSelections.set(ownerId, nextSelection);

    // Only a genuine selection change (or this owner already being current,
    // or nobody being current yet) claims "current owner." A re-render that
    // only touches display fields (e.g. marking an unrelated row attended)
    // must not silently steal ownership from another table the user is
    // actively selecting in — it still updates this owner's stored payload
    // above, in case it's promoted later, but doesn't publish or reassign.
    const shouldBecomeOwner =
      selectionChanged ||
      this.applicantTableOwnerId === ownerId ||
      this.applicantTableOwnerId === null;
    if (shouldBecomeOwner) {
      this.applicantTableOwnerId = ownerId;
    }
    if (this.applicantTableOwnerId === ownerId) {
      this._publishApplicantTable(
        this._withOtherSelectionsFlag(applicantTable, ownerId),
      );
    }
  }

  // Only the current owner's selection is ever published (see the class
  // comment on applicantTableContexts) — this flags when another still-
  // mounted owner also has a real selection, so Cue can say so instead of
  // silently acting on less than the user sees on screen.
  _withOtherSelectionsFlag(applicantTable, currentOwnerId) {
    if (!applicantTable) return applicantTable;
    const hasOtherOpenSelections = [...this.applicantTableContexts].some(
      ([ownerId, payload]) =>
        ownerId !== currentOwnerId &&
        (payload?.bulkSelect ||
          (payload?.selectedApplicantIds?.length ?? 0) > 0),
    );
    return { ...applicantTable, hasOtherOpenSelections };
  }

  _publishApplicantTable(applicantTable) {
    const changed =
      JSON.stringify(this.pageSpecificContext.applicantTable) !==
      JSON.stringify(applicantTable);
    this.pageSpecificContext.applicantTable = applicantTable;
    if (changed && this.currentContext) {
      this.sendContextUpdate();
    }
  }

  sendContextUpdate() {
    if (!this.currentContext) return;

    const fullContext = {
      ...this.currentContext,
      ...this.pageSpecificContext,
      timestamp: Date.now(),
    };

    this.currentContext = fullContext;
    window.wxCopilotPageContext = fullContext;
    this.pushToCopilot(fullContext, 0);
  }

  flushPageContext(url, mountId, retryCount = 0) {
    const newContext = {
      url,
      title: typeof document !== 'undefined' ? document.title : undefined,
      timestamp: Date.now(),
      ...this.pageSpecificContext,
    };

    if (this.hasContextChanged(newContext)) {
      this.currentContext = newContext;
      window.wxCopilotPageContext = newContext;
    }

    this.pushToCopilot(newContext, retryCount);
  }

  pushToCopilot(context, retryCount = 0) {
    try {
      const copilotInstance =
        window.WxCopilotComponentInstances?.[this.mountId];

      if (copilotInstance?.updateEphemeralContext) {
        const contextPayload = {
          page: {
            url: context.url,
            title: context.title,
            timestamp: context.timestamp,
          },
        };

        if (context.workflow) {
          contextPayload.workflow = {
            id: context.workflow.workflowId,
            name: context.workflow.workflowName,
            currentStageId: context.workflow.currentStageId,
            currentStageName: context.workflow.currentStageName,
          };
        }

        if (context.applicant) {
          contextPayload.applicant = {
            id: context.applicant.applicantId,
            name: context.applicant.applicantName,
            email: context.applicant.applicantEmail,
            stage: context.applicant.stageId
              ? {
                  id: context.applicant.stageId,
                  title: context.applicant.stageName,
                }
              : undefined,
            opening: context.applicant.openingId
              ? {
                  id: context.applicant.openingId,
                  name: context.applicant.openingName,
                }
              : undefined,
          };
        }

        if (context.applicantTable) {
          contextPayload.applicantTable = {
            opening: context.applicantTable.openingId
              ? {
                  id: context.applicantTable.openingId,
                  name: context.applicantTable.openingName,
                }
              : undefined,
            currentStage: context.applicantTable.currentStageId
              ? {
                  id: context.applicantTable.currentStageId,
                  title: context.applicantTable.currentStageName,
                  type: context.applicantTable.currentStageType,
                }
              : undefined,
            stages: context.applicantTable.stages,
            applicants: context.applicantTable.applicants,
            totalApplicants: context.applicantTable.totalApplicants,
            // Empty here means either "nothing selected" or "select-all" —
            // bulkSelect (below) is what tells the two apart.
            selectedApplicantIds:
              context.applicantTable.selectedApplicantIds ?? [],
            bulkSelect: Boolean(context.applicantTable.bulkSelect),
            hasOtherOpenSelections: Boolean(
              context.applicantTable.hasOtherOpenSelections,
            ),
          };
        }

        copilotInstance.updateEphemeralContext(JSON.stringify(contextPayload));
      } else if (retryCount < this.MAX_RETRIES) {
        if (this.retryTimer) {
          clearTimeout(this.retryTimer);
        }
        this.retryTimer = setTimeout(() => {
          this.pushToCopilot(context, retryCount + 1);
        }, this.RETRY_DELAY);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[WxCopilot] Error updating page context:', error);
    }
  }

  hasContextChanged(newContext) {
    if (!this.currentContext) {
      return true;
    }

    return (
      this.currentContext.url !== newContext.url ||
      JSON.stringify(this.currentContext.workflow) !==
        JSON.stringify(newContext.workflow) ||
      JSON.stringify(this.currentContext.applicant) !==
        JSON.stringify(newContext.applicant) ||
      JSON.stringify(this.currentContext.applicantTable) !==
        JSON.stringify(newContext.applicantTable)
    );
  }

  clearPageSpecificContext() {
    this.pageSpecificContext = {};
    this.applicantTableOwnerId = null;
    this.applicantTableContexts.clear();
    this.applicantTableSelections.clear();
  }

  cleanup() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
  }
}

export const wxCopilotContextManager = new WxCopilotContextManager();
